/**
 * Sprint S13.1.B.1 — reusable multi-select via checkbox grid.
 *
 * MVP design: simple checkbox list (no Combobox / popover) — works для 5-15
 * options ranges typical в onboarding. Optional "other" free-text field
 * appends к selected values prefixed `other:` (caller handles serialization).
 */
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  hint?: string;
  error?: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  allowOther?: boolean;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  otherPlaceholder?: string;
}

export function FieldMultiSelect({
  label,
  hint,
  error,
  options,
  selected,
  onChange,
  allowOther = false,
  otherValue = "",
  onOtherChange,
  otherPlaceholder,
}: Props) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div>
      <span className="mb-1.5 block text-[13px] font-medium text-navy">
        {label}
      </span>
      {hint ? (
        <span className="mb-2 block text-[12px] text-taupe">{hint}</span>
      ) : null}
      <div className="space-y-2">
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition",
                checked
                  ? "border-amber/60 bg-amber/10"
                  : "border-cream-border bg-cream-light hover:border-amber/30",
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.value)}
                className="h-4 w-4 cursor-pointer accent-amber"
              />
              <span className="text-[14px] text-navy-deep">{opt.label}</span>
            </label>
          );
        })}
      </div>
      {allowOther ? (
        <input
          type="text"
          value={otherValue}
          onChange={(e) => onOtherChange?.(e.target.value)}
          placeholder={otherPlaceholder}
          className="mt-2 w-full rounded-lg border border-cream-border bg-cream-light px-4 py-2 text-[14px] text-navy-deep placeholder:text-taupe-light focus:border-amber/40 focus:outline-none focus:ring-2 focus:ring-amber/60"
        />
      ) : null}
      {error ? (
        <span className="mt-1 block text-[12px] text-coral">{error}</span>
      ) : null}
    </div>
  );
}
